import { useState, useEffect, useMemo } from 'react';
import API, { fmt } from '@/lib/api';
import { toast } from 'sonner';
import { Sparkle, Globe, MagnifyingGlass, CheckCircle, Warning } from '@phosphor-icons/react';
import HsCodeReviewModal from './HsCodeReviewModal';

const LANDEN = [
  { code: 'CN', label: '🇨🇳 China' },
  { code: 'TR', label: '🇹🇷 Turkije' },
  { code: 'IN', label: '🇮🇳 India' },
  { code: 'US', label: '🇺🇸 VS' },
  { code: 'VN', label: '🇻🇳 Vietnam' },
  { code: 'BD', label: '🇧🇩 Bangladesh' },
  { code: 'GB', label: '🇬🇧 VK' },
  { code: 'KR', label: '🇰🇷 Zuid-Korea' },
  { code: 'JP', label: '🇯🇵 Japan' },
];

export default function ImportTaricPanel({ artikels, onUpdate }) {
  const [zoek, setZoek] = useState('');
  const [oorsprong, setOorsprong] = useState('CN');
  const [busyId, setBusyId] = useState(null);
  const [tariefBusyId, setTariefBusyId] = useState(null);
  const [tarieven, setTarieven] = useState({}); // {hs_code: {invoerrecht_pct, btw_pct, ...}}
  const [bulkBusy, setBulkBusy] = useState(false);
  const [reviewModal, setReviewModal] = useState(null); // { artikel, suggestie, gebruikt_materiaal }

  // Pre-load tarieven uit artikel.import_tarief (persistent veld)
  useEffect(() => {
    const m = {};
    (artikels || []).forEach(a => {
      if (a.import_tarief && (a.import_tarief.invoerrecht_pct !== undefined || a.import_tarief.invoerrecht_pct === 0)) {
        m[a.id] = a.import_tarief;
      }
    });
    setTarieven(m);
  }, [artikels]);

  // Filter: enkel artikels die mogelijk import-relevant zijn (zonder HS-code OF aankoop > 0)
  const filtered = useMemo(() => {
    const q = zoek.toLowerCase().trim();
    return artikels.filter(a => {
      if (!q) return true;
      return (a.omschrijving || '').toLowerCase().includes(q)
        || (a.art_code || '').toLowerCase().includes(q)
        || (a.hs_code || '').includes(q);
    });
  }, [artikels, zoek]);

  const zonderHsCode = artikels.filter(a => !(a.hs_code || '').trim()).length;
  const metHsCode = artikels.length - zonderHsCode;

  // Bereken totale extra kost van invoerrechten over alle artikels met geladen tarief
  const totalen = useMemo(() => {
    let aankoopTot = 0;
    let invoerrechtTot = 0;
    let aantalMetTarief = 0;
    artikels.forEach(a => {
      const t = tarieven[a.id];
      const aank = parseFloat(a.aankoopprijs) || 0;
      if (t && aank > 0) {
        aankoopTot += aank;
        invoerrechtTot += aank * (parseFloat(t.invoerrecht_pct) || 0) / 100;
        aantalMetTarief++;
      }
    });
    return { aankoopTot, invoerrechtTot, aantalMetTarief };
  }, [artikels, tarieven]);

  const findHs = async (a) => {
    setBusyId(a.id);
    try {
      const r = await API.post('/artikels/find-hscode', {
        omschrijving: a.omschrijving,
        categorie: a.categorie,
        foto_url: a.foto_url,
      });
      if (!r.data?.ok) {
        toast.error('AI kon geen HS-code voorstellen', { description: r.data?.error });
        return;
      }
      setReviewModal({ artikel: a, suggestie: r.data, gebruikt_materiaal: '' });
    } catch (e) {
      toast.error('Fout', { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusyId(null);
    }
  };

  const pasToe = async (hsCode, meta) => {
    if (!reviewModal) return;
    const artikel = reviewModal.artikel;
    try {
      await API.put(`/artikels/${artikel.id}`, {
        hs_code: hsCode,
        oorsprong_land: artikel.oorsprong_land || oorsprong,
        hs_beschrijving: meta?.beschrijving || '',
        hs_motivatie: meta?.motivatie || '',
      });
      toast.success(`HS-code ${hsCode} opgeslagen`);
      setReviewModal(null);
      onUpdate?.();
    } catch (e) {
      toast.error('Opslaan mislukt', { description: e?.response?.data?.detail || e.message });
    }
  };

  const lookupTarief = async (a) => {
    if (!a.hs_code) return;
    setTariefBusyId(a.id);
    try {
      const r = await API.get(`/artikels/hscode-tarief/${a.hs_code}`, { params: { oorsprong } });
      if (!r.data?.ok) {
        toast.error('Tarief niet gevonden', { description: r.data?.error });
        return;
      }
      setTarieven(prev => ({ ...prev, [a.id]: r.data }));
      // Persist op artikel zelf
      try {
        await API.put(`/artikels/${a.id}`, {
          import_tarief: {
            oorsprong: r.data.oorsprong,
            invoerrecht_pct: r.data.invoerrecht_pct,
            btw_pct: r.data.btw_pct,
            antidumping: r.data.antidumping || '',
            opmerkingen: r.data.opmerkingen || '',
            updated_at: new Date().toISOString(),
          },
        });
      } catch { /* niet kritiek */ }
    } catch (e) {
      toast.error('Fout', { description: e?.response?.data?.detail || e.message });
    } finally {
      setTariefBusyId(null);
    }
  };

  const bulkFind = async () => {
    const lege = artikels.filter(a => !(a.hs_code || '').trim());
    if (lege.length === 0) {
      toast.info('Alle artikels hebben al een HS-code');
      return;
    }
    if (!window.confirm(`${lege.length} artikels zonder HS-code. Doorgaan met AI-bulk lookup? (~${Math.ceil(lege.length * 1.5)} sec)`)) return;
    setBulkBusy(true);
    let ok = 0;
    for (const a of lege) {
      try {
        const r = await API.post('/artikels/find-hscode', {
          omschrijving: a.omschrijving,
          categorie: a.categorie,
          foto_url: a.foto_url,
        });
        if (r.data?.ok && r.data.hs_code) {
          await API.put(`/artikels/${a.id}`, {
            hs_code: r.data.hs_code,
            hs_beschrijving: r.data.beschrijving || '',
            hs_motivatie: r.data.motivatie || '',
          });
          ok++;
        }
      } catch { /* skip */ }
    }
    setBulkBusy(false);
    toast.success(`${ok}/${lege.length} HS-codes ingevuld`);
    onUpdate?.();
  };

  return (
    <div data-testid="import-taric-tab" className="space-y-4">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 p-4 rounded-lg">
        <div className="flex items-start gap-3">
          <Globe size={24} weight="duotone" className="text-blue-700 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-base font-bold text-blue-900">Import & TARIC tarieven</div>
            <div className="text-xs text-blue-800 mt-0.5 leading-relaxed">
              AI suggereert HS-codes (8-cijfer EU CN-codes) o.b.v. omschrijving + foto, en geeft een indicatief invoertarief per oorsprongsland.
              <span className="block mt-1 font-medium">⚠️ Adviserend — voor definitieve douaneaangifte raadpleeg TARIC of vraag een BIB aan.</span>
            </div>
          </div>
        </div>
      </div>

      {/* KPI-tegels: kostprijs invoerrechten over alle artikels met geladen tarief */}
      {totalen.aantalMetTarief > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="taric-kpis">
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Aankoopprijs totaal</div>
            <div className="text-xl font-bold text-gray-900 mt-1">{fmt(totalen.aankoopTot)}</div>
            <div className="text-[10px] text-gray-400 mt-0.5">{totalen.aantalMetTarief} artikel(s) met tarief</div>
          </div>
          <div className="bg-rose-50 border-2 border-rose-200 rounded-lg p-3" data-testid="taric-kpi-invoerrecht">
            <div className="text-[10px] uppercase tracking-wider text-rose-700 font-bold">+ Kostprijs invoerrechten</div>
            <div className="text-xl font-bold text-rose-700 mt-1">+ {fmt(totalen.invoerrechtTot)}</div>
            <div className="text-[10px] text-rose-600 mt-0.5">extra douane-kost per inkoopcyclus</div>
          </div>
          <div className="bg-emerald-50 border-2 border-emerald-200 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">= Werkelijke landed cost</div>
            <div className="text-xl font-bold text-emerald-700 mt-1">{fmt(totalen.aankoopTot + totalen.invoerrechtTot)}</div>
            <div className="text-[10px] text-emerald-600 mt-0.5">aankoop + invoerrecht (excl. BTW)</div>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 p-4 rounded-lg">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-sm">
            <span className="font-bold text-gray-900">{metHsCode}</span>
            <span className="text-gray-500"> met HS-code</span>
            <span className="mx-2 text-gray-300">·</span>
            <span className="font-bold text-amber-700">{zonderHsCode}</span>
            <span className="text-gray-500"> zonder</span>
          </div>
          <div className="flex-1" />
          <select className="field-input" style={{ maxWidth: 200 }} value={oorsprong} onChange={e => setOorsprong(e.target.value)} data-testid="oorsprong-select">
            {LANDEN.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
          <input
            className="field-input"
            style={{ maxWidth: 240 }}
            placeholder="Zoek artikel of HS-code…"
            value={zoek}
            onChange={e => setZoek(e.target.value)}
            data-testid="taric-zoek"
          />
          <button
            className="btn btn-primary btn-sm inline-flex items-center gap-1.5"
            onClick={bulkFind}
            disabled={bulkBusy || zonderHsCode === 0}
            data-testid="bulk-find-hscode"
          >
            <Sparkle size={14} weight="duotone" />
            {bulkBusy ? 'Bezig…' : `🤖 AI bulk-lookup (${zonderHsCode})`}
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr className="text-xs uppercase tracking-wider text-gray-500">
              <th className="px-3 py-2 text-left">Artikel</th>
              <th className="px-3 py-2 text-left">HS-code</th>
              <th className="px-3 py-2 text-left">Oorsprong</th>
              <th className="px-3 py-2 text-right">Aankoop</th>
              <th className="px-3 py-2 text-right">Invoer-recht</th>
              <th className="px-3 py-2 text-right">Kostprijs incl.</th>
              <th className="px-3 py-2 text-right">BTW</th>
              <th className="px-3 py-2 text-left">Antidumping</th>
              <th className="px-3 py-2 text-right">Acties</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 200).map(a => {
              const t = tarieven[a.id];
              const aank = parseFloat(a.aankoopprijs) || 0;
              const recht_pct = t ? (parseFloat(t.invoerrecht_pct) || 0) : 0;
              const recht_eur = t && aank > 0 ? aank * recht_pct / 100 : 0;
              const kostprijs_incl = aank + recht_eur;
              return (
                <tr key={a.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <div className="font-semibold text-gray-900 truncate" style={{ maxWidth: 320 }}>{a.omschrijving}</div>
                    <div className="text-[10px] text-gray-400 font-mono">{a.art_code}</div>
                  </td>
                  <td className="px-3 py-2">
                    {a.hs_code ? (
                      <span className="font-mono text-xs bg-blue-50 text-blue-800 px-2 py-0.5 rounded font-bold" data-testid={`hs-${a.art_code}`}>
                        {a.hs_code}
                      </span>
                    ) : (
                      <span className="text-amber-600 text-xs flex items-center gap-1">
                        <Warning size={12} weight="duotone" /> Niet ingevuld
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600">{a.oorsprong_land || oorsprong}</td>
                  <td className="px-3 py-2 text-right text-xs text-gray-700">
                    {aank > 0 ? fmt(aank) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right" data-testid={`invoerrecht-${a.art_code}`}>
                    {t ? (
                      <div>
                        <div className="font-bold text-gray-900">{t.invoerrecht_pct}%</div>
                        {aank > 0 && (
                          <div className="text-[10px] text-rose-600 font-semibold">+ {fmt(recht_eur)}</div>
                        )}
                      </div>
                    ) : <span className="text-gray-300 text-xs">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right" data-testid={`kostprijs-incl-${a.art_code}`}>
                    {t && aank > 0 ? (
                      <span className="font-bold text-emerald-700">{fmt(kostprijs_incl)}</span>
                    ) : <span className="text-gray-300 text-xs">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {t ? <span className="text-gray-700">{t.btw_pct}%</span> : <span className="text-gray-300 text-xs">—</span>}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600 truncate" style={{ maxWidth: 220 }}>
                    {t ? (t.antidumping || '—') : ''}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-1.5">
                      {!a.hs_code ? (
                        <button
                          className="btn btn-outline btn-xs inline-flex items-center gap-1"
                          onClick={() => findHs(a)}
                          disabled={busyId === a.id}
                          data-testid={`find-hs-${a.art_code}`}
                        >
                          <Sparkle size={11} weight="duotone" />
                          {busyId === a.id ? '…' : '🤖 Zoek'}
                        </button>
                      ) : (
                        <button
                          className="btn btn-outline btn-xs inline-flex items-center gap-1"
                          onClick={() => lookupTarief(a)}
                          disabled={tariefBusyId === a.id}
                          data-testid={`lookup-tarief-${a.art_code}`}
                        >
                          <MagnifyingGlass size={11} weight="duotone" />
                          {tariefBusyId === a.id ? '…' : 'Tarief'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="text-center py-8 text-gray-400 text-sm">Geen artikels gevonden</td></tr>
            )}
          </tbody>
        </table>
        {filtered.length > 200 && (
          <div className="p-3 text-center text-xs text-gray-500 bg-gray-50 border-t">
            Toont eerste 200 van {filtered.length}. Verfijn met zoekvak.
          </div>
        )}
      </div>

      <div className="text-[11px] text-gray-400 italic">
        <CheckCircle size={11} weight="bold" className="inline mr-1 text-emerald-600" />
        Tarieven via Gemini AI op basis van publieke EU TARIC-kennis. Niet bindend voor douaneaangifte.
      </div>

      {/* Review modal: AI-suggestie + alternatieven, gebruiker bevestigt */}
      <HsCodeReviewModal
        artikel={reviewModal?.artikel}
        suggestie={reviewModal?.suggestie}
        gebruiktMateriaal={reviewModal?.gebruikt_materiaal}
        onApply={pasToe}
        onClose={() => setReviewModal(null)}
        onSuggestieChange={(nieuwe, materiaal) =>
          setReviewModal((prev) => prev ? { ...prev, suggestie: nieuwe, gebruikt_materiaal: materiaal } : prev)
        }
      />
    </div>
  );
}
